import React, { useState } from 'react'
import { isMobile } from 'react-device-detect'
import { GalleryItem as GalleryItemComponent } from './GalleryItem'
import { VideoDownloadModal } from './VideoDownloadModal'

import { StyleClipItem, GalleryItem } from '../types'

interface StyleClipGridProps {
  items: StyleClipItem[]
  onDownload: (item: StyleClipItem) => void
  onDelete?: (item: StyleClipItem) => void
  isLoading?: boolean
  // 페이지네이션 관련
  currentPage?: number
  totalPages?: number
  onNextPage?: () => void
  onPrevPage?: () => void
}

export const StyleClipGrid: React.FC<StyleClipGridProps> = ({
  items,
  onDownload,
  onDelete,
  isLoading = false,
  currentPage,
  totalPages,
  onNextPage,
  onPrevPage,
}) => {
  const itemsPerPage = 16

  // 비디오 다운로드 모달 상태 관리
  const [selectedItem, setSelectedItem] = useState<StyleClipItem | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentModalIndex, setCurrentModalIndex] = useState(0)

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedItem(null)
  }

  const handleNavigateModal = (index: number) => {
    if (index >= 0 && index < items.length) {
      setCurrentModalIndex(index)
      setSelectedItem(items[index])
    }
  }

  // 비디오 클릭 핸들러 (VideoDownloadModal 열기)
  const handleItemClick = (item: StyleClipItem) => {
    const itemIndex = items.findIndex((i) => i.id === item.id)
    setSelectedItem(item)
    setCurrentModalIndex(itemIndex)
    setIsModalOpen(true)
  }

  const renderLoadingSkeletons = () => {
    return Array.from({ length: itemsPerPage }).map((_, index) => (
      <div
        key={`skeleton-${index}`}
        className="h-[248px] w-[160px] animate-pulse rounded-[10px] bg-gray-300"
      />
    ))
  }

  const renderEmptyState = () => {
    return (
      <div className="col-span-full flex flex-col items-center justify-center py-20">
        <div className="text-center">
          <p className="mb-2 text-[18px] font-bold text-[#2E343D]">
            아직 생성된 Style Clip이 없어요!
          </p>
          <p className="text-[14px] text-[#8F929B]">
            Style Shot에서 비디오를 생성하거나 AI 스타일룸에서 Style Clip을
            만들어보세요.
          </p>
        </div>
      </div>
    )
  }

  // StyleClipItem을 GalleryItem으로 변환하는 헬퍼 함수
  const convertToGalleryItem = (item: StyleClipItem): GalleryItem => item

  return (
    <div className="w-full">
      <div
        className={`grid justify-items-center gap-1 ${isMobile ? 'grid-cols-2' : 'grid-cols-5'} `}
      >
        {isLoading
          ? renderLoadingSkeletons()
          : items.length > 0
            ? items.map((item) => (
                <GalleryItemComponent
                  key={item.id}
                  item={convertToGalleryItem(item)}
                  onDownload={() => onDownload(item)}
                  onVideoDownload={() => handleItemClick(item)}
                  // Style Clip에서는 비디오 생성 기능과 전체 이미지 보기 기능 없음
                  onDirectVideoCreate={undefined}
                  onShowFullImage={undefined}
                  onDelete={onDelete ? () => onDelete(item) : undefined}
                />
              ))
            : renderEmptyState()}
      </div>

      {/* 영상 다운로드 모달 */}
      <VideoDownloadModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        item={selectedItem ? convertToGalleryItem(selectedItem) : null}
        allItems={items.map(convertToGalleryItem)}
        currentIndex={currentModalIndex}
        onNavigate={handleNavigateModal}
        // Style Clip에서는 비디오 생성 기능 없음
        onVideoGenerate={undefined}
        onDelete={
          onDelete && selectedItem ? () => onDelete(selectedItem) : undefined
        }
        currentPage={currentPage}
        totalPages={totalPages}
        onNextPage={onNextPage}
        onPrevPage={onPrevPage}
      />
    </div>
  )
}
